/**
 * Make antd's STATIC APIs work under React 19.
 *
 * `message.*`, `notification.*` and `Modal.confirm` render themselves into a
 * detached container, and antd v5 does that through `ReactDOM.render` — removed
 * in React 19. This app is React 19.2 with antd 5.29, so every one of those
 * calls has been a silent no-op: no "Invalid verification code", no "Verification
 * code sent", no save confirmation, and `Modal.confirm` never resolving (which
 * is why "Unsubscribe all" did nothing at all — its promise had no other
 * resolver). Roughly 20 call sites across the portal, all quietly dead.
 *
 * antd exposes `unstableSetRender` for exactly this, so no extra dependency is
 * needed — `@ant-design/v5-patch-for-react-19` does the same thing in a
 * package. Imported for side effects from the app entry, before anything can
 * call a static API.
 *
 * PRO-635.
 */
import { unstableSetRender } from 'antd';
import { createRoot, Root } from 'react-dom/client';

type RootHolder = Element | DocumentFragment | { _reactRoot?: Root };

unstableSetRender((node, container) => {
  const holder = container as RootHolder & { _reactRoot?: Root };
  holder._reactRoot ||= createRoot(container);
  const root = holder._reactRoot;

  root.render(node);

  return async () => {
    // Let React finish the current commit before tearing the root down;
    // unmounting synchronously inside a render warns and can drop the exit
    // animation.
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
    root.unmount();
  };
});
